/*
  Shared feeding maths: resting energy requirement (RER), the maintenance
  energy factor (MER), and daily kcal.

  WHY THIS FILE EXISTS
  --------------------
  The same RER x MER calculation was already written twice, independently and
  identically, in sections/custom-pet-meal-calculator.liquid and
  sections/bonpet-meal-plan-builder.liquid. Adding a third inline copy for the
  PDP widget would have been a third thing to drift. New surfaces call this.

  The two older surfaces still carry their own inline copies - migrating them
  is deliberately NOT part of the PDP widget change, because it would move
  live numbers on two other pages in a PR about a buy-box widget. See the
  DOG MER note below for the one place they now disagree with this file.

  RER = 70 * bodyweight_kg ^ 0.75          (standard allometric formula)
  MER = RER * factor                        (factor from the table below)

  DOG MER - THIS FILE DELIBERATELY DIFFERS FROM THE TWO OLDER SURFACES
  --------------------------------------------------------------------
  Both older implementations give an average-activity dog 1.8 when neutered
  and 1.6 when intact. That is transposed: neutering lowers maintenance energy
  requirement, it does not raise it. The cat row directly above it in both
  files has the relationship the right way round (1.2 neutered, 1.4 intact),
  and correcting the dog row makes it structurally match the cat row - intact
  at average activity lands on the same factor as high activity, exactly as it
  does for cats. Corrected here; the older two still need their own change.
*/
(function (root) {
  'use strict';

  // Activity arrives either in the meal-plan-builder's vocabulary
  // ('chilled' / 'plays' / 'energy', see its ACTIVITY tiles) or in the plain
  // words the PDP widget uses. Both collapse to 1 (low) / 2 (average) /
  // 3 (high) so a profile saved by the builder can prefill the widget.
  function activityLevel(activity) {
    if (activity === 'chilled' || activity === 'low' || activity === 1) return 1;
    if (activity === 'plays' || activity === 'average' || activity === 2) return 2;
    if (activity === 'energy' || activity === 'high' || activity === 3) return 3;
    return null;
  }

  function merFactor(pet) {
    // Growth outranks everything: a puppy or kitten is 2.0 regardless of
    // activity or neuter status.
    if (pet.underOneYear) return 2;

    var act = activityLevel(pet.activity);
    if (act === null) return null;

    var neutered = pet.neutered !== false;

    if (pet.species === 'cat') {
      if (act === 1) return 1;
      if (act === 2) return neutered ? 1.2 : 1.4;
      return 1.4;
    }

    if (act === 1) return 1.4;
    if (act === 2) return neutered ? 1.6 : 1.8;
    return 1.8;
  }

  function restingEnergy(weightKg) {
    return 70 * Math.pow(weightKg, 0.75);
  }

  // Returns null rather than NaN whenever an input is missing or nonsensical,
  // so callers can render an empty state instead of "NaN kcal".
  function dailyKcal(pet) {
    var weight = Number(pet && pet.weightKg);
    if (!isFinite(weight) || weight <= 0) return null;

    var factor = merFactor(pet);
    if (factor === null) return null;

    return Math.round(restingEnergy(weight) * factor);
  }

  // Packs of THIS product per day. packKcal is the figure on the pack, read
  // out of tbp_nutrients_meta at render time - never parsed from the product
  // description, which is how the 500 g listing's stale kcal leaks in.
  function packsPerDay(pet, packKcal) {
    var kcal = dailyKcal(pet);
    var perPack = Number(packKcal);
    if (kcal === null || !isFinite(perPack) || perPack <= 0) return null;
    return kcal / perPack;
  }

  root.TBPFeeding = {
    activityLevel: activityLevel,
    merFactor: merFactor,
    restingEnergy: restingEnergy,
    dailyKcal: dailyKcal,
    packsPerDay: packsPerDay
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
